import { setDocumentStatus } from '@features/workspace/store';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { connectApi } from '@app/api';
import _ from 'lodash';
import moment from 'moment';
import { selectActiveCompanyId } from '@features/workspace/store/selectors';
import { IAppDispatch, IRootState } from '@app/store';
import {
  fetchEmployee,
  fetchUser,
  fetchCompany,
  fetchCompanyDocuments,
} from './fetch';

export const updateCompany = createAsyncThunk<any, any, { state: IRootState }>(
  'company/update',
  async ({ companyId, data }) => {
    return (await connectApi.company.update(companyId, data))?.data?.payload;
  }
);

export const updateCompanyAddress = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('company/update-address', async ({ companyId, data }) => {
  return (await connectApi.company.updateAddress(companyId, data))?.data
    ?.payload;
});

export const updateUser = createAsyncThunk<
  any,
  any,
  { state: IRootState; dispatch: IAppDispatch }
>('user/update', async ({ userId, data }, { getState, dispatch }) => {
  const userData = _.omit(data, [
    'person',
    'address',
    'assignment',
    'position',
    'documents',
  ]);
  const addressData = data.address;
  const personData = _.omit(data.person, ['identityDocuments']);
  const identityDocumentsData = data.person.identityDocuments;
  const firstIdentityDocumentData = identityDocumentsData[0];
  const assignment = data.assignment;
  const position = data.position;

  const companyId = selectActiveCompanyId(getState());

  if (firstIdentityDocumentData && !firstIdentityDocumentData.issuingDate) {
    firstIdentityDocumentData.issuingDate = moment()
      .add(-1, 'years')
      .format('YYYY-MM-DD HH:mm:ss');
  }
  if (firstIdentityDocumentData && !firstIdentityDocumentData.expirationDate) {
    firstIdentityDocumentData.expirationDate = moment()
      .add(10, 'years')
      .format('YYYY-MM-DD HH:mm:ss');
  }

  await Promise.all([
    connectApi.user.update(userId, {
      phoneNumber: userData.phoneNumber,
    }),
    connectApi.user.updatePerson(userId, personData),
    addressData?.id
      ? connectApi.user.updateAddress(userId, addressData)
      : connectApi.user.createAddress(userId, addressData),
    firstIdentityDocumentData?.id
      ? connectApi.user.updatePersonIdentity(
          userId,
          firstIdentityDocumentData.id,
          firstIdentityDocumentData
        )
      : connectApi.user.createPersonIdentity(userId, firstIdentityDocumentData),
    assignment
      ? connectApi.employee.reassign(companyId ?? '', userId, position)
      : connectApi.employee.assign(companyId ?? '', userId, position),
  ]);

  dispatch(fetchUser(userId));
  dispatch(fetchEmployee({ companyId, userId }));
});

export const updateUserAddress = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('user/update-address', async ({ userId, data }) => {
  return (await connectApi.user.updateAddress(userId, data))?.data?.payload;
});

export const blockUnblockUser = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('user/block-unblock', async ({ userId, block = true }) => {
  return connectApi.user.blockUnblock(userId, block);
});

export const sendIDVerificationEmail = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('user/send-jumio-verify', async ({ userId, companyId }) => {
  return (await connectApi.user.sendIDVerificationEmail(userId, companyId))
    ?.data?.payload;
});

export const sendResetPasswordEmail = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('user/send-reset-password-email', async ({ userId }) => {
  return (await connectApi.user.sendResetPasswordEmail(userId))?.data?.payload;
});

export const updateDocumentStatus = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('document/update-status', async ({ documentId, status }, { dispatch }) => {
  dispatch(
    setDocumentStatus({
      documentId,
      status,
    })
  );

  return (await connectApi.document.updateDocumentStatus(documentId, status))
    ?.data?.payload;
});

export const updateDocument = createAsyncThunk<
  any,
  { documentId: string; documentType: number; userId: string | null },
  { state: IRootState; dispatch: IAppDispatch }
>(
  'document/update',
  async ({ documentId, documentType, userId }, { dispatch }) => {
    const response = await connectApi.document.update(documentId, {
      documentType,
      userId: userId || '',
    });

    dispatch(fetchCompanyDocuments(null));

    return response?.data?.payload;
  }
);

export const reassignEmployee = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>(
  'employee/reassign',
  async ({ userId, companyId, position }, { dispatch }) => {
    return (await connectApi.employee.reassign(companyId, userId, position))
      ?.data?.payload;
  }
);

export const setPrimaryUser = createAsyncThunk<
  any,
  any,
  { state: IRootState; dispatch: IAppDispatch }
>('company/set-primary-user', async ({ companyId, userId }, { dispatch }) => {
  const response = await connectApi.company.setPrimaryUser(companyId, userId);

  // Refetch company data to update primaryUserId
  await dispatch(fetchCompany(companyId));

  return response?.data?.payload;
});

// BANK ACCOUNTS
export const blockTraderootAccount = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('traderoot/block-account', async ({ companyId, accountNumber }) => {
  return (await connectApi.bankAccount.blockAccount(companyId, accountNumber))
    ?.data;
});

export const unblockTraderootAccount = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('traderoot/unblock-account', async ({ companyId, accountNumber }) => {
  return (await connectApi.bankAccount.unblockAccount(companyId, accountNumber))
    ?.data;
});
